import { BROWSER_ROUTES } from "@constants/browser.routes";
import { USER_TYPES } from "@constants/user.roles";
import type { User } from "@features/Login/types";
import { getAuthToken } from "@utils/auth";

/** Routes faculty users are allowed to access. */
export const FACULTY_ALLOWED_ROUTES: readonly string[] = [
	BROWSER_ROUTES.STUDENT_ATTENDANCE,
	BROWSER_ROUTES.ATTENDANCE,
	BROWSER_ROUTES.PROMOTION,
	BROWSER_ROUTES.DROPOUT,
	BROWSER_ROUTES.EMPLOYMENT_STATUS,
	BROWSER_ROUTES.RESULT_MANAGEMENT,
	BROWSER_ROUTES.SESSION_STUDENTS,
	BROWSER_ROUTES.STUDENT_CERTIFICATES,
] as const;

export const FACULTY_HOME_ROUTE = BROWSER_ROUTES.STUDENT_ATTENDANCE;

type UserTypeSource = Pick<User, "userTypeName" | "userType"> | null | undefined;

type FacultyTokenClaims = {
	userType?: string;
	instituteId?: number | string;
	instituteUuid?: string;
	facultyMemberId?: number | string;
	facultyMemberUuid?: string;
};

/** Decode JWT payload (no signature verification — claims used for client routing only). */
export const decodeAccessTokenPayload = (token: string | null | undefined): FacultyTokenClaims | null => {
	if (!token) return null;
	try {
		const [, payload] = token.split(".");
		if (!payload) return null;
		const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
		return JSON.parse(atob(padded)) as FacultyTokenClaims;
	} catch {
		return null;
	}
};

/** Resolves portal user type from login/profile user payload. */
export const getUserTypeName = (user: UserTypeSource, token?: string | null): string | null => {
	if (user) {
		const fromName = user.userTypeName?.trim().toLowerCase();
		if (fromName) return fromName;
		const fromNested = user.userType?.name?.trim().toLowerCase();
		if (fromNested) return fromNested;
	}
	const fromToken = decodeAccessTokenPayload(token ?? getAuthToken())?.userType?.trim().toLowerCase();
	return fromToken || null;
};

export const isFacultyUser = (user: UserTypeSource, token?: string | null): boolean => {
	return getUserTypeName(user, token) === USER_TYPES.FACULTY;
};

export const isFacultyAllowedRoute = (pathname: string): boolean => {
	return FACULTY_ALLOWED_ROUTES.some(
		route => pathname === route || pathname.startsWith(`${route}/`),
	);
};

export type FacultyInstituteContext = {
	instituteUuid: string | null;
	/** Numeric institute id — preferred for courses / students / results APIs (same as institute-owner flow). */
	instituteId: number | string | null;
	instituteName: string | null;
	facultyMemberUuid: string | null;
	facultyMemberName: string | null;
};

/** Institute identity from faculty login payload, nested facultyMember, and JWT claims. */
export const getFacultyInstituteContext = (
	user: User | null | undefined,
	token?: string | null,
): FacultyInstituteContext => {
	const accessToken = token ?? getAuthToken();
	const claims = decodeAccessTokenPayload(accessToken);

	if (!isFacultyUser(user, accessToken)) {
		return {
			instituteUuid: null,
			instituteId: null,
			instituteName: null,
			facultyMemberUuid: null,
			facultyMemberName: null,
		};
	}

	const instituteUuid =
		user?.instituteUuid?.trim() ||
		user?.facultyMember?.institute?.uuid?.trim() ||
		(typeof claims?.instituteUuid === "string" ? claims.instituteUuid.trim() : null) ||
		null;

	const rawInstituteId = user?.instituteId ?? claims?.instituteId ?? null;
	const instituteId =
		rawInstituteId === null || rawInstituteId === undefined || rawInstituteId === ""
			? null
			: rawInstituteId;

	const instituteName = user?.facultyMember?.institute?.name?.trim() || null;
	const facultyMemberUuid =
		user?.facultyMember?.uuid?.trim() ||
		(typeof claims?.facultyMemberUuid === "string" ? claims.facultyMemberUuid.trim() : null) ||
		(typeof user?.uuid === "string" ? user.uuid.trim() : null) ||
		null;
	const facultyMemberName =
		user?.facultyMember?.name?.trim() ||
		[user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
		null;

	return {
		instituteUuid,
		instituteId,
		instituteName,
		facultyMemberUuid,
		facultyMemberName,
	};
};

/**
 * ID to pass into institute-scoped list APIs (courses, admissions, results).
 * Prefer numeric instituteId (matches institute-owner pages); fall back to uuid.
 */
export const getFacultyApiInstituteId = (context: FacultyInstituteContext): string | number | "" => {
	if (context.instituteId !== null && context.instituteId !== undefined && context.instituteId !== "") {
		return context.instituteId;
	}
	return context.instituteUuid || "";
};

/** Resolve institute id for faculty from stored user + access token (login / JWT). */
export const resolveFacultyApiInstituteId = (
	user: User | null | undefined,
	token?: string | null,
): string | number | "" => {
	return getFacultyApiInstituteId(getFacultyInstituteContext(user, token));
};
